/**
 * MCPTab — Super Admin → MCP operational dashboard.
 * Read-only KPIs aggregated from mcp_audit_logs / mcp_oauth_tokens.
 * Reuses the existing SuperAdmin styling. NO new analytics system.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Activity, Loader2, RefreshCw, AlertTriangle, Power, ShieldAlert, Check, X, Download, Server, History, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, hint, danger }) {
  return (
    <div
      data-testid={`mcp-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`rounded-xl border p-4 ${danger ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}
    >
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${danger ? 'text-red-700' : 'text-slate-900'}`}>{value}</div>
      {hint && <div className="text-xs text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export default function MCPTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kill, setKill] = useState({ enabled: false });
  const [toggling, setToggling] = useState(false);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [breakers, setBreakers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      const [dash, ks, cl, al, ph, tl, cb] = await Promise.all([
        axios.get(`${API}/mcp/admin/dashboard`, cfg),
        axios.get(`${API}/mcp/admin/kill-switch`, cfg),
        axios.get(`${API}/mcp/admin/clients`, cfg),
        axios.get(`${API}/mcp/admin/alerts`, cfg),
        axios.get(`${API}/mcp/admin/providers/health`, cfg),
        axios.get(`${API}/mcp/admin/timeline?limit=80`, cfg),
        axios.get(`${API}/mcp/admin/circuit-breakers`, cfg),
      ]);
      setData(dash.data);
      setKill(ks.data);
      setClients(Array.isArray(cl.data) ? cl.data : []);
      setAlerts(Array.isArray(al.data?.alerts) ? al.data.alerts : []);
      setProviders(Array.isArray(ph.data) ? ph.data : []);
      setTimeline(Array.isArray(tl.data) ? tl.data : []);
      setBreakers(Array.isArray(cb.data) ? cb.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load MCP dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleKill = async () => {
    const next = !kill.enabled;
    const verb = next ? 'ENABLE the kill switch (all MCP requests will return 503)' : 'DISABLE the kill switch (MCP will resume)';
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    setToggling(true);
    try {
      const token = localStorage.getItem('token');
      const { data: r } = await axios.put(`${API}/mcp/admin/kill-switch`,
        { enabled: next },
        { headers: { Authorization: `Bearer ${token}` } });
      setKill(r);
      toast.success(next ? 'MCP kill switch ENABLED' : 'MCP kill switch disabled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kill switch toggle failed');
    } finally {
      setToggling(false);
    }
  };

  const toggleClient = async (client, allow) => {
    if (!allow && !window.confirm(`Block ${client.client_name}? All currently-active tokens for this client will be revoked.`)) return;
    try {
      const token = localStorage.getItem('token');
      const { data: r } = await axios.put(`${API}/mcp/admin/clients/${client.client_id}`,
        { allowed: allow }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${client.client_name} ${r.allowed ? 'allowed' : 'blocked'}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const resolveAlert = async (alert) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/mcp/admin/alerts/${alert.id}/resolve`,
        { resolved: true }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Alert marked resolved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/mcp/admin/audit-export?limit=50000`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `mcp-audit-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit CSV exported');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  const resetBreaker = async (clientId) => {
    if (!window.confirm(`Override circuit breaker for ${clientId}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/mcp/admin/circuit-breakers/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Circuit breaker reset for ${clientId}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    }
  };

  const statusBadge = (status) => {
    const map = {
      Healthy: 'bg-emerald-100 text-emerald-700',
      Warning: 'bg-amber-100 text-amber-700',
      Degraded: 'bg-red-100 text-red-700',
      Offline: 'bg-slate-100 text-slate-600',
    };
    return <Badge className={`${map[status] || map.Offline} hover:${map[status] || map.Offline}`}>{status}</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-16 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading MCP dashboard…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="mcp-admin-dashboard">
      {kill.enabled && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
          <AlertTriangle size={18} />
          <div className="text-sm font-medium">
            MCP Emergency Kill Switch is ENABLED — all `/api/mcp` and OAuth requests return 503.
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Activity size={18} /> MCP Operations (24 h)
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            data-testid="mcp-refresh-btn"
          >
            {refreshing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
            Refresh
          </Button>
          <Button
            variant={kill.enabled ? 'default' : 'outline'}
            size="sm"
            onClick={toggleKill}
            disabled={toggling}
            data-testid="mcp-kill-switch-toggle"
            className={kill.enabled
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : 'border-red-300 text-red-700 hover:bg-red-50'}
          >
            {toggling ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Power size={14} className="mr-1" />}
            {kill.enabled ? 'Disable Kill Switch' : 'Emergency Disable MCP'}
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="mcp-export-csv">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Connected tenants" value={data.connected_tenants} />
        <Stat label="AI providers" value={data.connected_ai_providers} hint={data.connected_clients?.join(', ') || '—'} />
        <Stat label="Requests (24h)" value={data.requests_last_24h.toLocaleString()} />
        <Stat label="Active tokens" value={data.active_tokens} />
        <Stat label="Success" value={`${data.success_pct_24h}%`} hint={`${data.success_count_24h} ok`} />
        <Stat
          label="Failure"
          value={`${data.failure_pct_24h}%`}
          hint={`${data.failure_count_24h} failed`}
          danger={data.failure_pct_24h > 5}
        />
        <Stat label="429 triggers" value={data.rate_limit_429_triggered_24h} danger={data.rate_limit_429_triggered_24h > 0} />
        <Stat label="Avg latency" value={`${data.avg_latency_ms_24h} ms`} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Top MCP tools (24 h)</CardTitle></CardHeader>
        <CardContent>
          {data.top_tools_24h.length === 0 ? (
            <div className="text-sm text-slate-500 py-2">No tool invocations in the last 24 hours.</div>
          ) : (
            <table className="w-full text-sm" data-testid="mcp-top-tools-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-slate-500">
                  <th className="py-2 font-medium">Tool</th>
                  <th className="py-2 font-medium">Calls</th>
                  <th className="py-2 font-medium">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {data.top_tools_24h.map((t) => (
                  <tr key={t.tool_name} className="border-b border-slate-50">
                    <td className="py-2 font-mono text-xs">{t.tool_name}</td>
                    <td className="py-2"><Badge variant="outline">{t.count}</Badge></td>
                    <td className="py-2 text-slate-600">{t.avg_latency_ms} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* MCP Client Allowlist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Server size={16} /> Provider Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="mcp-provider-health">
            {providers.map((p) => (
              <div key={p.client_id} className="rounded-lg border p-3" data-testid={`mcp-provider-${p.client_id}`}>
                <div className="flex justify-between items-start">
                  <div className="font-semibold">{p.client_name}</div>
                  {statusBadge(p.status)}
                </div>
                <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                  <div>Tenants connected: <span className="text-slate-800 font-medium">{p.connected_tenants}</span></div>
                  <div>Active tokens: <span className="text-slate-800 font-medium">{p.active_tokens}</span></div>
                  <div>Failed auth (1h): <span className={p.failed_auth_pct_1h >= 5 ? 'text-red-600 font-medium' : 'text-slate-700'}>{p.failed_auth_pct_1h}%</span></div>
                  <div>Last activity: {p.last_activity ? new Date(p.last_activity).toLocaleString() : '—'}</div>
                  {p.circuit_tripped && <div className="text-red-600 font-medium">Circuit breaker open</div>}
                  {p.blocked && <div className="text-red-600 font-medium">Blocked by super admin</div>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Circuit Breakers */}
      {breakers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Zap size={16} /> Circuit Breakers</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm" data-testid="mcp-circuit-breakers-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-slate-500">
                  <th className="py-2 font-medium">Client</th>
                  <th className="py-2 font-medium">Failures</th>
                  <th className="py-2 font-medium">Tripped until</th>
                  <th className="py-2 font-medium">Status</th>
                  <th className="py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {breakers.map((b) => (
                  <tr key={b.client_id} className="border-b border-slate-50">
                    <td className="py-2 font-mono text-xs">{b.client_id}</td>
                    <td className="py-2">{b.failure_count}</td>
                    <td className="py-2 text-xs">{b.tripped_until ? new Date(b.tripped_until).toLocaleString() : '—'}</td>
                    <td className="py-2">
                      {b.currently_tripped ? (
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Open</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Closed</Badge>
                      )}
                    </td>
                    <td className="py-2">
                      {b.currently_tripped && (
                        <Button size="sm" variant="outline" onClick={() => resetBreaker(b.client_id)}
                          data-testid={`mcp-cb-reset-${b.client_id}`}>
                          Override
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* MCP Client Allowlist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">MCP Client Allowlist</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm" data-testid="mcp-client-allowlist-table">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-500">
                <th className="py-2 font-medium">Client</th>
                <th className="py-2 font-medium">Source</th>
                <th className="py-2 font-medium">Connected Tenants</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.client_id} className="border-b border-slate-50" data-testid={`mcp-client-row-${c.client_id}`}>
                  <td className="py-2 font-medium">{c.client_name}</td>
                  <td className="py-2 text-xs text-slate-500">{c.source}</td>
                  <td className="py-2">{c.connected_tenants}</td>
                  <td className="py-2">
                    {c.allowed ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Allowed</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Blocked</Badge>
                    )}
                  </td>
                  <td className="py-2">
                    <Switch
                      checked={c.allowed}
                      onCheckedChange={(v) => toggleClient(c, !!v)}
                      data-testid={`mcp-client-toggle-${c.client_id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Security Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert size={16} /> Security Alerts
            {alerts.length > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{alerts.length} open</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-sm text-slate-500 py-2 flex items-center gap-2">
              <Check size={14} className="text-emerald-600" /> No active security alerts.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="mcp-security-alerts-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-slate-500">
                  <th className="py-2 font-medium">Severity</th>
                  <th className="py-2 font-medium">Tenant</th>
                  <th className="py-2 font-medium">Client</th>
                  <th className="py-2 font-medium">Event</th>
                  <th className="py-2 font-medium">Timestamp</th>
                  <th className="py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="py-2">
                      <Badge className={a.severity === 'HIGH' ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}>
                        {a.severity}
                      </Badge>
                    </td>
                    <td className="py-2 font-mono text-xs">{(a.tenant_id || '—').slice(0, 12)}</td>
                    <td className="py-2 text-xs">{a.client_id || '—'}</td>
                    <td className="py-2 text-xs">{a.event_type} ({a.event_count})</td>
                    <td className="py-2 text-xs text-slate-500">{a.last_event_at ? new Date(a.last_event_at).toLocaleString() : ''}</td>
                    <td className="py-2">
                      <Button size="sm" variant="outline" onClick={() => resolveAlert(a)} data-testid={`mcp-alert-resolve-${a.id}`}>
                        <X size={12} className="mr-1" /> Resolve
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Incident Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><History size={16} /> Incident Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {timeline.length === 0 ? (
            <div className="text-sm text-slate-500 py-2">No events yet.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="mcp-incident-timeline">
              {timeline.slice(0, 50).map((ev, i) => (
                <div key={`${ev.timestamp}-${ev.type}-${i}`} className="flex items-start gap-3 text-xs border-l-2 pl-3"
                  style={{ borderColor: ev.severity === 'HIGH' ? '#dc2626' : ev.severity === 'MEDIUM' ? '#d97706' : '#94a3b8' }}>
                  <div className="text-slate-500 whitespace-nowrap min-w-[150px]">
                    {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}
                  </div>
                  <div className="flex-1">
                    <span className="font-mono font-medium text-slate-800">{ev.type}</span>
                    {ev.client_id && <span className="ml-2 text-slate-600">client={ev.client_id}</span>}
                    {ev.tenant_id && <span className="ml-2 text-slate-500">tenant={ev.tenant_id.slice(0, 10)}</span>}
                    {ev.actor && <span className="ml-2 text-slate-500">by {ev.actor}</span>}
                    {ev.detail && <span className="ml-2 text-slate-400">— {ev.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
